import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Button,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import DialogActions from "@mui/material/DialogActions";
import * as XLSX from "xlsx";
import { getHeaders } from "../../helpers/exportUtils";
import AlertBar from "./AlertBar";
import secureLocalStorage from "react-secure-storage";
import CircularProgress from "@mui/material/CircularProgress";
import { API_URL } from "../../config/environment";
import { useTranslation } from "react-i18next";

const CreateSheet = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [technofunctionality, setTechnofunctionality] = useState(null);
  const [categories, setCategories] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState(false);
  const [isloading, setIsLoading] = useState(false);

  const handleChange = (event) => {
    setTechnofunctionality(event.target.value);
  };

  function handleOnClose() {
    setTechnofunctionality(null);
    onClose();
  }

  function stringToArrayBuffer(s) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
    return buf;
  }

  const createExcelSheet = () => {
    setIsLoading(true);
    if (!technofunctionality) {
      setErrors(true);
      setErrorMessage("No Technofunctionality selected");
      setIsLoading(false);
      return;
    }

    try {
      // Prepare headers
      const headers = [getHeaders(technofunctionality)]; // Headers only

      // Create a new workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(headers);
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

      // Generate Excel file as a Blob
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });
      const blob = new Blob([stringToArrayBuffer(wbout)], {
        type: "application/octet-stream",
      });

      const currentDate = new Date().toISOString()?.split("T")[0];
      const currentTime = new Date()
        .toISOString()
        ?.split("T")[1]
        ?.split(".")[0];
      const filename = `${technofunctionality}_${currentDate}_${currentTime}`;

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.xlsx`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 0);
    } catch (error) {
      setErrors(true);
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  async function getCategories() {
    const url = `${API_URL}/categories?categoryType=technofunctionality`;

    const response = await fetch(url);
    const data = await response.json();

    if (data.status === "success") {
      const categories = data.categories;
      return categories;
    } else {
      console.log("Could not fetch data from server");
      setErrors(true);
      setErrorMessage(data.message);
    }
  }

  useEffect(() => {
    //  let active = true;

    //  if (!loading) {
    //    return undefined;
    //  }

    // const _categories = JSON.parse(storedCategories);
    // if (_categories) {
    //   const defaultOption = t("dashboard.create-sheet.default-option");
    //   _categories.unshift(defaultOption);
    //   setCategories(_categories);
    //   return;
    // }

    (async () => {
      const categories = await getCategories();
      if (categories) {
        const defaultOption = t("dashboard.create-sheet.default-option");
        const _categories = JSON.stringify(categories);
        secureLocalStorage.setItem("categories", _categories);
        categories.unshift(defaultOption);
        setCategories([...categories]);
      }
    })();

    // TODO: Implement a cleanup to abort requests using AbortController
    //  return () => {
    //    active = false;
    //  };
  }, [t]);

  const dialogStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(5px)",
  };

  const closeButtonStyle = {
    position: "absolute",
    top: 0,
    right: 0,
    margin: "16px", // Add margin as needed
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        style={dialogStyle}
        maxWidth="xl"
        fullWidth={true}
      >
        {/* Close button */}
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleOnClose}
          style={closeButtonStyle}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle>
          <Typography variant="h5" gutterBottom fontWeight={700}>
            Create Sheet
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Display current step */}

          <Typography variant="h6" gutterBottom fontWeight={300} fontSize={14}>
            This creates an Excel sheet template for the selected
            technofunctionality
          </Typography>

          <FormControl sx={{ m: 1, minWidth: 500 }}>
            <InputLabel id="export-data-label">Technofunctionality</InputLabel>
            <Select
              labelId="export-data-lable"
              id="export-data-lable"
              value={technofunctionality}
              required
              label="Technofunctionality"
              placeholder="Select your technfunctionality"
              onChange={handleChange}
              name="upload-sheet"
              error={errors}
              helperText={errors && "Technofunctionality is required"}
            >
              {categories &&
                categories.map((category) => (
                  <MenuItem value={category}>{category}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            //   endIcon={<AddIcon />}
            color="success"
            //   component={Link}
            onClick={createExcelSheet}
            disabled={technofunctionality ? false : true}
            sx={{ margin: "20px" }}
          >
            Create
            {isloading && <CircularProgress size={20} color="inherit" />}
          </Button>
        </DialogActions>
      </Dialog>
      <AlertBar
        open={errors}
        message={errorMessage}
        severity={"error"}
        onClose={() => setErrors(false)}
      />
    </>
  );
};

export default CreateSheet;
